import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../config/supabase';
import LoadingSpinner from '../components/LoadingSpinner';

const ContentAdminLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Check for existing session on component mount
  useEffect(() => {
    checkExistingSession();
  }, []);

  const checkExistingSession = async () => {
    const lastLogin = localStorage.getItem('lastLoginTime');
    const sevenDays = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

    if (lastLogin && Date.now() - parseInt(lastLogin) < sevenDays) {
      setLoading(true);
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        // Verify content admin status
        const { data: adminData } = await supabase
          .from('content_admins')
          .select('*')
          .eq('user_id', session.user.id)
          .eq('is_active', true)
          .single();

        if (adminData) {
          localStorage.setItem('contentAdmin', JSON.stringify(adminData));
          navigate('/content-admin');
          return;
        }
      }
    }
    // Clear expired session
    localStorage.removeItem('lastLoginTime');
    localStorage.removeItem('contentAdmin');
    setLoading(false);
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Sign in with Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: formData.email,
        password: formData.password
      });

      if (authError) throw authError;

      if (authData.user) {
        // Check if user is a content admin
        const { data: adminData, error: adminError } = await supabase
          .from('content_admins')
          .select('*')
          .eq('user_id', authData.user.id)
          .eq('is_active', true)
          .single();

        if (adminError || !adminData) {
          await supabase.auth.signOut();
          throw new Error('Access denied. Content admin privileges required.');
        }

        // Store admin info in localStorage
        localStorage.setItem('contentAdmin', JSON.stringify(adminData));
        
        // Store login timestamp if "Keep me logged in" is checked
        if (rememberMe) {
          localStorage.setItem('lastLoginTime', Date.now().toString());
        }
        
        // Redirect to content admin dashboard
        navigate('/content-admin');
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%)',
      padding: '20px'
    }}>
      <div style={{
        background: '#ffffff',
        borderRadius: '20px',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        padding: '40px',
        maxWidth: '450px',
        width: '100%'
      }}>
        {/* Logo/Header */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '80px',
            height: '80px',
            margin: '0 auto 16px',
            background: 'linear-gradient(135deg, #CDB688 0%, #CDB688 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 16px rgba(205, 182, 136, 0.3)'
          }}>
            <i className="fas fa-newspaper" style={{
              fontSize: '36px',
              color: '#ffffff'
            }}></i>
          </div>
          <h1 style={{
            fontSize: '28px',
            fontWeight: '700',
            color: '#1a1a1a',
            marginBottom: '8px'
          }}>
            Content Admin Portal
          </h1>
          <p style={{
            color: '#666666',
            fontSize: '14px'
          }}>
            Manage News & Events
          </p>
        </div>

        {error && (
          <div style={{
            background: '#fee',
            border: '1px solid #fcc',
            borderRadius: '10px',
            padding: '12px 16px',
            marginBottom: '24px',
            color: '#c33',
            fontSize: '14px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <i className="fas fa-exclamation-circle"></i>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#333333'
            }}>
              <i className="fas fa-envelope" style={{marginRight: '8px', color: '#CDB688'}}></i>
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="admin@example.com"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e0e0e0',
                borderRadius: '10px',
                fontSize: '14px',
                transition: 'all 0.3s ease',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#CDB688'}
              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'block',
              marginBottom: '8px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#333333'
            }}>
              <i className="fas fa-lock" style={{marginRight: '8px', color: '#CDB688'}}></i>
              Password
            </label>
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              placeholder="Enter your password"
              style={{
                width: '100%',
                padding: '12px 16px',
                border: '2px solid #e0e0e0',
                borderRadius: '10px',
                fontSize: '14px',
                transition: 'all 0.3s ease',
                outline: 'none'
              }}
              onFocus={(e) => e.target.style.borderColor = '#CDB688'}
              onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              cursor: 'pointer',
              fontSize: '14px',
              color: '#666666'
            }}>
              <input
                type="checkbox"
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={{
                  marginRight: '8px',
                  cursor: 'pointer',
                  width: '16px',
                  height: '16px',
                  accentColor: '#CDB688'
                }}
              />
              Keep me logged in for 7 days
            </label>
          </div>

          <button
            type="submit"
            disabled={loading}
            style={{
              width: '100%',
              padding: '14px',
              background: 'linear-gradient(135deg, #CDB688 0%, #CDB688 100%)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '10px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: loading ? 'not-allowed' : 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 4px 12px rgba(205, 182, 136, 0.3)',
              marginBottom: '16px'
            }}
            onMouseOver={(e) => {
              if (!loading) {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 16px rgba(205, 182, 136, 0.4)';
              }
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 4px 12px rgba(205, 182, 136, 0.3)';
            }}
          >
            {loading ? (
              <>
                <i className="fas fa-spinner fa-spin" style={{marginRight: '8px'}}></i>
                Signing in...
              </>
            ) : (
              <>
                <i className="fas fa-sign-in-alt" style={{marginRight: '8px'}}></i>
                Sign In
              </>
            )}
          </button>

          <div style={{
            textAlign: 'center',
            fontSize: '13px',
            color: '#666666'
          }}>
            <Link 
              to="/login" 
              style={{
                color: '#CDB688',
                textDecoration: 'none',
                fontWeight: '600'
              }}
            >
              <i className="fas fa-arrow-left" style={{marginRight: '6px'}}></i>
              Back to User Login
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ContentAdminLogin;
